import { Box, Drawer, Stack } from "@mui/material";
// @mui
import { styled, useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// components
import Logo from "@/components/Logo";
import Scrollbar from "@/components/Scrollbar";
import { NavSectionVertical } from "@/components/nav-section";
// config
import { NAVBAR } from "@/config";
import useCollapseDrawer from "@/hooks/useCollapseDrawer";
// hooks
import useResponsive from "@/hooks/useResponsive";
import useUserPermissionsSync, {
    UserData,
    addUserDataListener,
    getUserDataCache,
} from "@/hooks/useUserPermissionsSync";
import useAuth from "@/providers/hooks/useAuthProvider";
// utils
import cssStyles from "@/utils/cssStyles";
import { checkUserPermissions } from "@/utils/permissionUtils";

import CollapseButton from "./CollapseButton";
//
import SidebarConfig from "./NavConfig";
import NavbarAccount from "./NavbarAccount";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        flexShrink: 0,
        transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.shorter,
        }),
    },
}));

// ----------------------------------------------------------------------

interface NavbarVerticalProps {
    isOpenSidebar: boolean;
    onCloseSidebar: () => void;
}

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: NavbarVerticalProps) {
    const theme = useTheme();
    const { userData: authUserData } = useAuth();
    const [cachedUserData, setCachedUserData] = useState<UserData | null>(getUserDataCache());

    // For initial load, usePermissionsRefresh is called, but variables are not used
    // This way, initial data load occurs, but linter errors are not generated
    useUserPermissionsSync(60000, false);

    // Listen for cache changes
    useEffect(() => {
        // On first render, get data from cache
        setCachedUserData(getUserDataCache());

        // Add a listener for cache changes
        const unsubscribe = addUserDataListener(() => {
            const newData = getUserDataCache();
            setCachedUserData(newData);
        });

        return unsubscribe;
    }, []);

    // Use both sources of data
    const effectiveUserData = cachedUserData || authUserData;

    // Original navConfig
    const allNavConfig = SidebarConfig();

    // Filtered navConfig based on permissions
    const navConfig = useMemo(() => {
        // User permissions
        const userPermissions = effectiveUserData?.permissionCodes || [];

        // Filter sections
        return allNavConfig
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => {
                    // Elements that do not require permissions are always visible
                    if (!item.permissions || item.permissions.length === 0) {
                        return true;
                    }

                    // Check for elements that require permissions
                    return checkUserPermissions(userPermissions, item.permissions);
                }),
            }))
            .filter((section) => section.items.length > 0); // Remove empty sections
    }, [allNavConfig, effectiveUserData]);

    const { pathname } = useLocation();

    const isDesktop = useResponsive("up", "lg");

    const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
        useCollapseDrawer();

    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar();
        }
    }, [pathname]);

    const renderContent = (
        <Scrollbar
            sx={{
                height: 1,
            }}
        >
            <Stack
                spacing={3}
                sx={{
                    pt: 3,
                    pb: 2,
                    px: 2.5,
                    flexShrink: 0,
                    ...(isCollapse && { alignItems: "center" }),
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Logo
                        sx={{
                            width: 180,
                            height: "auto",
                            ...(isCollapse && {
                                width: 45,
                                height: 45,
                            }),
                        }}
                        square={isCollapse}
                    />

                    {isDesktop && !isCollapse && (
                        <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
                    )}
                </Stack>

                <NavbarAccount isCollapse={isCollapse} />
            </Stack>

            <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />

            <Box sx={{ flexGrow: 1, pb: 5 }} />
        </Scrollbar>
    );

    return (
        <RootStyle
            sx={{
                width: {
                    lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
                },
                ...(collapseClick && {
                    position: "absolute",
                }),
            }}
        >
            {!isDesktop && (
                <Drawer
                    open={isOpenSidebar}
                    onClose={onCloseSidebar}
                    PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
                >
                    {renderContent}
                </Drawer>
            )}

            {isDesktop && (
                <Drawer
                    open
                    variant="persistent"
                    onMouseEnter={onHoverEnter}
                    onMouseLeave={onHoverLeave}
                    PaperProps={{
                        sx: {
                            width: NAVBAR.DASHBOARD_WIDTH,
                            borderRightStyle: "dashed",
                            bgcolor: "background.default",
                            transition: (theme) =>
                                theme.transitions.create("width", {
                                    duration: theme.transitions.duration.standard,
                                }),
                            ...(isCollapse && {
                                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
                            }),
                            ...(collapseHover && {
                                ...cssStyles(theme).bgBlur(),
                                boxShadow: (theme) => theme.shadows[24],
                            }),
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            )}
        </RootStyle>
    );
}
